import { ReactElement } from "react";
import Slider from "react-slick";
import Head from "next/head";
import { MainLayout } from "@/containers/layout/LandingLayout";
import CustomersReviews from "@/components/customer-reviews/CustomersReviews";
import AccordingToCustomers from "@/components/customer-reviews/AccordingToCustomers";
import Testimonials from "@/components/customer-reviews/Testimonials";
import { Props } from "@/components/howitworks/components/types";
import PartnerWithKeyzy from "@/components/customer-reviews/Partners";
import ContactBanner from "@/components/customer-reviews/ContactBanner";
import BannerReview from "@/components/customer-reviews/BannerReview/BannerReview";
import LatestArticles from "@/components/blog/latestArticles/LatestArticles";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import getConfig from "next/config";
import ReferralBanner from "@/components/landing-new/ReferralBanner";
import Image from "next/image";
import Screen from "@/constants/screens";
import { useWindowSize } from "@/shared/hooks/useWindowSize";

const CustomerReviews = ({ theme = "dark" }: Props): JSX.Element => {
  const { publicRuntimeConfig } = getConfig();
  const { width } = useWindowSize();
  const settings = {
    dots: false,
    arrows: false,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    centerMode: true,
    centerPadding: "200px",
    autoplay: true,
    responsive: [
      {
        breakpoint: 750,
        settings: {
          centerPadding: "170px",
        },
      },
      {
        breakpoint: 700,
        settings: {
          centerPadding: "150px",
        },
      },
      {
        breakpoint: 630,
        settings: {
          centerPadding: "120px",
        },
      },
      {
        breakpoint: 570,
        settings: {
          centerPadding: "90px",
        },
      },
      {
        breakpoint: 520,
        settings: {
          centerPadding: "60px",
        },
      },
      {
        breakpoint: 470,
        settings: {
          centerPadding: "40px",
        },
      },
      {
        breakpoint: 425,
        settings: {
          centerPadding: "20px",
        },
      },
    ],
  };
  return (
    <>
      <Head>
        <title>Reviews and Testimonials from Keyzy Customers and Partners</title>
        <link rel="canonical" href="https://www.keyzy.com/customer-reviews" />
        <meta
          name="description"
          key="description"
          content="Discover what our customers and brokers have to say about us. Check out our excellent Trustpilot rating, customer reviews, and broker testimonials."
        />
        <meta
          name="twitter:card"
          content="Discover what our customers and brokers have to say about us. Check out our excellent Trustpilot rating, customer reviews, and broker testimonials."
          key="twcard"
        />
        <meta name="twitter:creator" content="keyzyhomes" key="twhandle" />
        <meta property="og:url" content="/" key="ogurl" />
        <meta property="og:image" content="/contact/meta-og-contact-us.png" key="ogimage" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="Keyzy" key="ogsitename" />
        <meta
          property="og:title"
          content="Reviews and Testimonials from Keyzy Customers and Partners"
          key="ogtitle"
        />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_GB" />
        <meta
          property="og:description"
          content="Discover what our customers and brokers have to say about us. Check out our excellent Trustpilot rating, customer reviews, and broker testimonials."
          key="ogdesc"
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: "https://keyzy.com/" },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: "Customer Reviews",
                  item: "https://keyzy.com/customer-reviews",
                },
              ],
            }),
          }}
        />
      </Head>

      <div className="bg-white w-full">
        <CustomersReviews />
        <div className="flex relative flex-row justify-center">
          <div className="flex absolute h-1/2 bg-landing-blue w-full z-0"></div>
          <div className="hidden lg:flex flex-row gap-7 max-w-auto lg:max-w-7xl z-10 overflow-hidden">
            <BannerReview
              name={"James"}
              content={
                "“Superb friendly efficient company who supported us every step of the way with constant updates etc. Highly recommend anyone who is struggling to save a deposit join Keyzy they will make your dream home a reality.”"
              }
            />
            <BannerReview
              name={"Emma"}
              content={
                "“After a couple of viewings we eventually found home that fulfilled our dreams. Being able to actually chose a home in the area we wanted and to meet the needs of our young family meant everything to us.”"
              }
            />
            <BannerReview
              name={"Ashna"}
              content={
                "“Keyzy made our moving experience seamless. The communication from the team was excellent at every stage. It’s a great facility that no one else offers. They take care of the whole process from start to finish!”"
              }
            />
          </div>
          <div className="w-full lg:hidden block h-full">
            <Slider {...settings} className="customerReviewSlider">
              <div className="mx-2 justify-between h-[18.125rem] xs:h-[16.625rem] md:h-[15.125rem]">
                <BannerReview
                  name={"James"}
                  content={
                    "“Superb friendly efficient company who supported us every step of the way with constant updates etc. Highly recommend anyone who is struggling to save a deposit join Keyzy they will make your dream home a reality.”"
                  }
                />
              </div>
              <div className="mx-2 justify-between h-[18.125rem] xs:h-[16.625rem] md:h-[15.125rem]">
                <BannerReview
                  name={"Emma"}
                  content={
                    "“After a couple of viewings we eventually found home that fulfilled our dreams. Being able to actually chose a home in the area we wanted and to meet the needs of our young family meant everything to us.”"
                  }
                />
              </div>
              <div className="mx-2 justify-between h-[18.125rem] xs:h-[16.625rem] md:h-[15.125rem]">
                <BannerReview
                  name={"Ashna"}
                  content={
                    "“Keyzy made our moving experience seamless. The communication from the team was excellent at every stage. It’s a great facility that no one else offers. They take care of the whole process from start to finish!”"
                  }
                />
              </div>
            </Slider>
          </div>
        </div>
        <AccordingToCustomers />
        <section className="px-4 my-4 md:mb-12 bg-white overflow-hidden md:mx-6 lg:px-0 h-6 relative">
          <Image
            src={`/landing/${
              width < Screen.Tablet_768 ? "mobile-doodle.svg" : "tablet-doodle.svg"
            }`}
            alt="doodle"
            layout="fill"
            objectFit="contain"
          />
        </section>

        <Testimonials />
        <div className="left-0 flex mt-4  right-0 m overflow-hidden bg-repeat-x h-24 md:h-32 xl:h-48 bg-[url('/key-workers/city-outline-mobile.svg')]  md:bg-[url('/key-workers/city-outline-tablet.svg')] xl:bg-[url('/key-workers/city-outline-desktop.svg')]" />
        <LatestArticles
          selectedCategory={3 as number}
          excludeCategories={publicRuntimeConfig.NEXT_BLOG_LANDLORD_CATEGORY_ID}
        />
        <ContactBanner />
        {/* <MainReview /> */}
        <PartnerWithKeyzy />
        <ReferralBanner />
      </div>
    </>
  );
};

CustomerReviews.getLayout = function getLayout(page: ReactElement) {
  return <MainLayout>{page}</MainLayout>;
};

export default CustomerReviews;
